import React from 'react';
import { connect } from 'react-redux';
import ControlBoxView from './ControlBoxView';
import dividia_logo_white from '../images/dividia_white_blue.png';
import dividia_logo_dark from '../images/dividia_black_blue.png';
import { getTheme } from '../util/theme';
import { formatNvrClock } from '../util/formatClock';
import { formatClockInZone } from '../util/cloudTime';
import { getProductMode, PRODUCT_MODE } from '../backend/productMode';
import { getProductConfig } from '../backend';

/**
 * DesktopHeader — the desktop Live top header (redesign #86bb5dx62, Mock D4).
 * Replaces the old PageHeader on desktop. Markup + SVG paths transplanted from
 * `docs/java-parity-audit/mockups/live-mock-desktop.html`.
 *
 * Zones:
 *   LEFT   — DIVIDIA logo · clickable breadcrumb `system / view`.
 *            The breadcrumb is BOTH the always-visible current-system/view indicator
 *            AND the shortcut: system name → Jump panel, view name → Views panel
 *            (the same panels the left rail tabs open). "Best of both worlds" (David).
 *   CENTER — NVR clock, TIME ONLY (no "NVR" label), centered on the VIDEO container
 *            (offset by half the rail width) via CSS so it lands on the 4-up divider.
 *   RIGHT  — Live/Playback toggle · Setup (text, → Setup SCREEN) · account avatar.
 *
 * EV + serial + version are NOT here — they moved to the bottom-right (i) popover.
 *
 * Rendered inside `.live-desktop-header`, which CSS gates to desktop (min-width:1024),
 * so this whole header is desktop-only and never affects mobile/tablet.
 *
 * Props (from LiveView, which owns the panel + nav state):
 *   activePanel  — the open panel key ('jump' | 'views' | 'grid' | null)
 *   onOpenPanel(key) — toggle a rail panel (breadcrumb + rail share it)
 *   onSetup()    — open the Setup screen
 *   onPlayback() — guarded-nav to Playback
 *   viewLabel    — the current view's name (or 'Views')
 *   viewDirty    — unsaved live-arrangement changes → show the dot
 */
export class DesktopHeader extends React.Component {
    state = { accountOpen: false, theme: getTheme(), localClock: new Date() };

    componentDidMount() {
        document.addEventListener('mousedown', this.onDocMouseDown);
        window.addEventListener('themechange', this.onThemeChange);
        this._clockTimer = setInterval(() => {
            if (this.props.isCloud) {
                this.setState({ localClock: new Date() });
            }
        }, 1000);
    }
    componentWillUnmount() {
        document.removeEventListener('mousedown', this.onDocMouseDown);
        window.removeEventListener('themechange', this.onThemeChange);
        clearInterval(this._clockTimer);
    }
    onThemeChange = (e) => this.setState({ theme: (e && e.detail) || getTheme() });
    onDocMouseDown = (e) => {
        if (this.state.accountOpen && this._acct && !this._acct.contains(e.target)) {
            this.setState({ accountOpen: false });
        }
    };
    toggleAccount = () => this.setState((s) => ({ accountOpen: !s.accountOpen }));
    closeAccount = () => this.setState({ accountOpen: false });

    render() {
        const { sName, fEview, fPlayback, nvrTimestamp, nvrTimezone, sUserName, isCloud,
                activePanel, onOpenPanel, onSetup, onPlayback, onLive, viewLabel, viewDirty, page = 'live', reserveJump, playbackDisabled } = this.props;
        const { date: nvrDate, time: nvrTime } = formatNvrClock(nvrTimestamp);
        let clockDate, clockTime, clockZone = '';
        if (isCloud) {
            // Cloud renders "now" in the SITE zone (nvrTimezone from JSCustomerProject, #86bbm5p92)
            // with a short label (e.g. CDT); falls back to browser-local (no label) when unset.
            const now = this.state.localClock || new Date();
            const c = formatClockInZone(now, nvrTimezone);
            clockDate = c.date;
            clockTime = c.time;
            clockZone = c.abbrev;
        } else {
            clockDate = nvrDate;
            clockTime = nvrTime;
        }
        // Product indicator in the account menu (parallels the mobile CLOUD/EV badge + the
        // blue/green accent): cloud → "· Cloud", eview → "· eView", dview → none. #86bb5dx62.
        const productTag = isCloud ? ' · Cloud' : (fEview ? ' · eView' : '');
        const initial = (sUserName || '?').trim().charAt(0).toUpperCase() || '?';
        // Playback button = the fPlayback USER RIGHT, across every product (cloud/local/eview).
        // Legacy single-cam playback is retired; Playback IS the multi-cam view (/mcplayback),
        // labeled "Playback" everywhere. Rights come from state.user.fPlayback (getRights RPC
        // locally, /ve/login payload in cloud) — same check, sourced differently.
        const playbackShown = fPlayback === 1;
        // Jump (system-switching) is a LOCAL/eView concept — a login into another NVR. CLOUD's one
        // token already covers every accessible system (no per-system login/logout), and views are
        // cross-system, so there's no "system" to switch: the breadcrumb is VIEW-ONLY, no system
        // segment (David #86bb5dx62). Gate on the named capability, not the mode.
        // Playback reuses this header Views-only → passes canJump={false}; Live leaves it undefined → hasJump.
        const canJump = this.props.canJump !== undefined ? this.props.canJump : getProductConfig().hasJump;
        // Playback flips the toggle (Playback active, Live is the button); Live keeps the original.
        const isPlaybackPage = page === 'playback';

        return (
            <div className="live-dtop">
                {/* LEFT — logo + breadcrumb (system / view) */}
                <div className="live-dtop__left">
                    <img className="live-dtop__logo" alt="Dividia" src={this.state.theme === 'dark' ? dividia_logo_white : dividia_logo_dark} />
                    <span className="live-dtop__vr" />
                    {/* System crumb = the Jump trigger. Rendered ONLY when the product supports jump
                        (local/eView). Cloud omits it entirely → the breadcrumb is view-name-only. */}
                    { canJump ? (
                      <>
                        <button
                            className="live-crumb live-crumb--sys"
                            onClick={() => onOpenPanel('jump')}
                            title="Switch system (Jump)"
                            aria-expanded={activePanel === 'jump'}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" /></svg>
                            <span className="live-crumb__stack">
                                <span className="live-crumb__kind">System</span>
                                <span className="live-crumb__lab">{sName || ''}</span>
                            </span>
                            <span className="live-crumb__car">&#9662;</span>
                        </button>
                        <span className="live-dtop__sep">/</span>
                      </>
                    ) : reserveJump ? (
                      /* Playback omits Jump but RESERVES its space (visibility:hidden) so the centered clock
                         doesn't shift between Live and Playback (David #86bb7phhh). */
                      <>
                        <span className="live-crumb live-crumb--sys live-crumb--reserved" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" /></svg>
                            <span className="live-crumb__lab">{sName || ''}</span>
                            <span className="live-crumb__car">&#9662;</span>
                        </span>
                        <span className="live-dtop__sep live-dtop__sep--reserved" aria-hidden="true">/</span>
                      </>
                    ) : null}
                    <button
                        className="live-crumb live-crumb--view"
                        onClick={() => onOpenPanel('views')}
                        title="Change or edit view"
                        aria-expanded={activePanel === 'views'}
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" /></svg>
                        <span className="live-crumb__stack">
                            <span className="live-crumb__kind">View</span>
                            <span className="live-crumb__lab">{viewLabel}</span>
                        </span>
                        {viewDirty && <span className="live-crumb__dot" title="Unsaved changes" />}
                        <span className="live-crumb__car">&#9662;</span>
                    </button>
                </div>

                {/* CENTER — clock (date + time), centered on the video container */}
                <div className="live-dtop__clock">
                    <span className="live-dtop__date">{clockDate}</span>
                    <span className="live-dtop__time">{clockTime}</span>
                    {clockZone ? <span className="live-dtop__tz" title={nvrTimezone}>{clockZone}</span> : null}
                </div>

                {/* RIGHT — Live/Playback toggle · Setup · account */}
                <div className="live-dtop__right">
                    <div className="live-dtop__toggle" role="group" aria-label="Live or Playback">
                        <button
                            className={`live-dtop__toggle-btn${isPlaybackPage ? '' : ' live-dtop__toggle-btn--on'}`}
                            aria-pressed={!isPlaybackPage}
                            onClick={isPlaybackPage ? onLive : undefined}
                        >Live</button>
                        {(isPlaybackPage || playbackShown) && (
                            <button
                                className={`live-dtop__toggle-btn${isPlaybackPage ? ' live-dtop__toggle-btn--on' : ''}${!isPlaybackPage && playbackDisabled ? ' live-dtop__toggle-btn--disabled' : ''}`}
                                aria-pressed={isPlaybackPage}
                                onClick={isPlaybackPage ? undefined : onPlayback}
                            >Playback</button>
                        )}
                    </div>
                    <button className="live-dtop__setup" title="Setup screen" onClick={onSetup}>Setup</button>
                    <span className="live-dtop__vr" />
                    <div className="live-dtop__acct-wrap" ref={(el) => { this._acct = el; }}>
                        <button className="live-dtop__acct" title={sUserName || 'Account'} onClick={this.toggleAccount} aria-haspopup="menu" aria-expanded={this.state.accountOpen}>
                            <span className="live-dtop__acct-av">{initial}</span>
                            <span className="live-dtop__acct-car">&#9662;</span>
                        </button>
                        {this.state.accountOpen && (
                            <div className="live-dtop__acct-menu" role="menu">
                                <div className="live-dtop__acct-name">{sUserName}{productTag}</div>
                                <ControlBoxView hidePlayback hideSetup />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    const { sName, fEview } = state.server;
    const { sName: sUserName, fPlayback } = state.user;
    const { nvrTimestamp, nvrTimezone } = state.utility;
    return { sName, fEview, fPlayback, sUserName, nvrTimestamp, nvrTimezone, isCloud: getProductMode(state) === PRODUCT_MODE.CLOUD };
};

export default connect(mapStateToProps)(DesktopHeader);
